import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { scrollPageTo } from '@/lib/scroll';
import { useNavigate, useParams, useSearchParams } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import { BookOpen, ChevronLeft, ChevronRight, List, Palette, Pause, Play, Settings2 } from 'lucide-react';
import { useProgress, useSettings } from '@/lib/store';
import { usePlayer } from '@/features/player/audioEngine';
import { useReadingTimer } from '@/features/progress/useReadingTimer';
import { haptic, IconButton } from '@/components/ui';
import { AyahRow, MushafPage, usePageVerses } from './Mushaf';
import { AyahSheet } from './AyahSheet';
import { ReaderSettingsSheet } from './ReaderSettingsSheet';
import { TajweedLegend } from './Tajweed';
import { isTajweed, layoutOf } from './fonts';
import { chapter, pageOfVerse, parseKey, versesByChapter } from './api';
import { BASMALAH_INDOPAK, BASMALAH_QPC } from './script';

export function Reader() {
  const params = useParams();
  const [search] = useSearchParams();
  const nav = useNavigate();
  const { readingMode, translationId, followAudio, mushaf } = useSettings((s) => s.settings);
  const [legendOpen, setLegendOpen] = useState(false);
  const update = useSettings((s) => s.update);
  const lastRead = useProgress((s) => s.lastRead);
  const setLastRead = useProgress((s) => s.setLastRead);

  const surahParam = params.surah ? Number(params.surah) : null;
  const pageParam = params.page ? Number(params.page) : null;
  const resumeKey = search.get('resume') ? lastRead?.verseKey : null;
  const ayahParam = search.get('ayah') ? Number(search.get('ayah')) : null;

  // Page numbers are per print (the 1405 and 1421 pages break differently), so look them up by āyah.
  const layout = layoutOf(mushaf);
  const [page, setPage] = useState<number>(() => pageParam ?? (resumeKey ? pageOfVerse(lastRead!.verseKey, layout) : surahParam ? pageOfVerse(`${surahParam}:${ayahParam ?? 1}`, layout) : 1));
  const [surah, setSurah] = useState<number>(surahParam ?? (resumeKey ? parseKey(resumeKey).surah : 1));
  const [sheetKey, setSheetKey] = useState<string | null>(null);
  const [settingsOpen, setSettingsOpen] = useState(false);

  // Jump to an ayah (deep link) → find its page.
  useEffect(() => {
    if (surahParam && ayahParam) setPage(pageOfVerse(`${surahParam}:${ayahParam}`, layout));
  }, [surahParam, ayahParam]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { if (surahParam && !ayahParam) { setSurah(surahParam); setPage(pageOfVerse(`${surahParam}:1`, layout)); } }, [surahParam, ayahParam]); // eslint-disable-line react-hooks/exhaustive-deps

  const player = usePlayer();
  const current = player.current.verseKey;

  // Follow the reciter: turn the page / scroll to the ayah being recited.
  useEffect(() => {
    if (!followAudio || !current) return;
    if (readingMode === 'page') setPage(pageOfVerse(current, layout));
    else document.getElementById(`v-${current}`)?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }, [current, followAudio, readingMode, layout]);

  const pageVerses = usePageVerses(page, readingMode === 'page');
  const surahVerses = useQuery({ queryKey: ['surah', surah, translationId], queryFn: () => versesByChapter(surah, translationId), enabled: readingMode === 'list', staleTime: Infinity });

  // The surah shown in the header follows the page in page mode — but if you
  // opened a surah and its page starts with the end of the previous one
  // (Yā-Sīn begins mid-page 440), it's still the surah you opened.
  const onPage = pageVerses.data?.map((v) => parseKey(v.verse_key).surah) ?? [];
  const headerSurah = readingMode !== 'page' || !onPage.length ? surah : onPage.includes(surah) ? surah : onPage[0];
  // Once you page away from it, follow the page.
  useEffect(() => { if (readingMode === 'page' && onPage.length && !onPage.includes(surah)) setSurah(onPage[0]); }, [readingMode, onPage.join(), surah]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const v = readingMode === 'page' ? pageVerses.data?.[0] : null;
    if (v) setLastRead({ verseKey: v.verse_key, page });
  }, [page, pageVerses.data, readingMode, setLastRead]);

  const pagesForTimer = useMemo(() => (readingMode === 'page' ? [page] : [...new Set(surahVerses.data?.map((v) => v.page_number) ?? [])]), [page, readingMode, surahVerses.data]);
  useReadingTimer(pagesForTimer, headerSurah);

  const go = useCallback((delta: number) => {
    haptic();
    setPage((p) => Math.max(1, Math.min(604, p + delta)));
    scrollPageTo(0);
  }, []);

  // Swipe: in a printed mushaf the next page lies to the LEFT, so a swipe
  // to the right (finger moving right) goes forward.
  const touch = useRef<{ x: number; y: number } | null>(null);
  const onTouchStart = (e: React.TouchEvent) => { touch.current = { x: e.touches[0].clientX, y: e.touches[0].clientY }; };
  const onTouchEnd = (e: React.TouchEvent) => {
    if (!touch.current) return;
    const dx = e.changedTouches[0].clientX - touch.current.x;
    const dy = e.changedTouches[0].clientY - touch.current.y;
    touch.current = null;
    if (Math.abs(dx) > 60 && Math.abs(dx) > Math.abs(dy) * 1.5) go(dx > 0 ? 1 : -1);
  };
  useEffect(() => {
    const k = (e: KeyboardEvent) => { if (readingMode !== 'page') return; if (e.key === 'ArrowLeft') go(1); if (e.key === 'ArrowRight') go(-1); };
    window.addEventListener('keydown', k);
    return () => window.removeEventListener('keydown', k);
  }, [go, readingMode]);

  const onWord = useCallback((verseKey: string) => {
    // Tapping the ayah being recited jumps playback there; otherwise open actions.
    if (usePlayer.getState().track && usePlayer.getState().surah === parseKey(verseKey).surah) usePlayer.getState().seekAyah(verseKey);
    else setSheetKey(verseKey);
  }, []);

  const playing = player.status === 'playing' || player.status === 'gap';
  const togglePlay = () => {
    if (player.surah === headerSurah && player.track) { player.toggle(); return; }
    const first = readingMode === 'page' ? pageVerses.data?.find((v) => parseKey(v.verse_key).surah === headerSurah) : null;
    void player.play(headerSurah, { fromAyah: first ? parseKey(first.verse_key).ayah : 1 });
  };

  const ch = chapter(headerSurah);
  const sheetText = (readingMode === 'page' ? pageVerses.data : surahVerses.data)?.find((v) => v.verse_key === sheetKey)?.text_uthmani;

  return (
    <div className="min-h-dvh">
      <header className="glass sticky top-0 z-30 flex items-center gap-2 px-3 pb-2" style={{ paddingTop: 'calc(var(--safe-top) + 8px)' }}>
        <IconButton label="Back to index" onClick={() => nav('/quran')}><ChevronLeft size={20} /></IconButton>
        <div className="min-w-0 flex-1 text-center">
          <div className="truncate text-[16px] font-bold">{ch.n} <span className="font-arabic font-normal text-muted">{ch.ar}</span></div>
          <div className="text-[12px] text-muted">{readingMode === 'page' ? `Page ${page} · Juz ${pageVerses.data?.[0]?.juz_number ?? ''}` : `${ch.t} · ${ch.v} āyāt`}</div>
        </div>
        <IconButton label={readingMode === 'page' ? 'Switch to list view' : 'Switch to page view'} onClick={() => update({ readingMode: readingMode === 'page' ? 'list' : 'page' })}>
          {readingMode === 'page' ? <List size={18} /> : <BookOpen size={18} />}
        </IconButton>
        {isTajweed(mushaf) && <IconButton label="Tajweed colours" onClick={() => setLegendOpen(true)}><Palette size={18} /></IconButton>}
        <IconButton label="Reading settings" onClick={() => setSettingsOpen(true)}><Settings2 size={18} /></IconButton>
        <IconButton label={playing ? 'Pause' : 'Play'} onClick={togglePlay} active>{playing && player.surah === headerSurah ? <Pause size={18} /> : <Play size={18} />}</IconButton>
      </header>

      {readingMode === 'page' ? (
        <main className="mx-auto max-w-[620px] px-3 pt-3 page-pad !pt-3" onTouchStart={onTouchStart} onTouchEnd={onTouchEnd}>
          <MushafPage page={page} onWord={onWord} />
          <div className="mt-4 flex items-center justify-between gap-3">
            <button type="button" className="press flex items-center gap-1 rounded-full bg-fill px-4 py-2 text-[15px] font-semibold" onClick={() => go(1)} disabled={page >= 604}><ChevronLeft size={18} /> Next</button>
            <label className="flex items-center gap-2 text-[14px] text-muted">
              Page
              <input type="number" min={1} max={604} value={page} onChange={(e) => { const v = Number(e.target.value); if (v >= 1 && v <= 604) setPage(v); }}
                className="w-16 rounded-lg bg-fill px-2 py-1 text-center text-ink" />
            </label>
            <button type="button" className="press flex items-center gap-1 rounded-full bg-fill px-4 py-2 text-[15px] font-semibold" onClick={() => go(-1)} disabled={page <= 1}>Previous <ChevronRight size={18} /></button>
          </div>
        </main>
      ) : (
        <main className="mx-auto max-w-[760px] page-pad !pt-0">
          {surah !== 1 && surah !== 9 && (mushaf === 'indopak'
            ? <div className="ar-indopak py-6 text-center text-[28px]" dir="rtl">{BASMALAH_INDOPAK}</div>
            : <div className="py-6 text-center text-[28px] [font-family:UthmanicHafs]" dir="rtl">{BASMALAH_QPC}</div>)}
          {surahVerses.isLoading && <div className="p-8 text-center text-muted">Loading {ch.n}…</div>}
          {surahVerses.error && <div className="p-8 text-center text-muted">Couldn't load {ch.n}. Check your connection.</div>}
          {surahVerses.data?.map((v) => <AyahRow key={v.id} verse={v} onWord={onWord} onMore={setSheetKey} />)}
          <div className="flex justify-between p-4">
            {surah > 1 ? <button type="button" className="press rounded-full bg-fill px-4 py-2 font-semibold" onClick={() => { setSurah(surah - 1); nav(`/quran/${surah - 1}`); scrollPageTo(0); }}>← {chapter(surah - 1).n}</button> : <span />}
            {surah < 114 && <button type="button" className="press rounded-full bg-fill px-4 py-2 font-semibold" onClick={() => { setSurah(surah + 1); nav(`/quran/${surah + 1}`); scrollPageTo(0); }}>{chapter(surah + 1).n} →</button>}
          </div>
        </main>
      )}

      <AyahSheet verseKey={sheetKey} text={sheetText} onClose={() => setSheetKey(null)} />
      <ReaderSettingsSheet open={settingsOpen} onClose={() => setSettingsOpen(false)} />
      <TajweedLegend open={legendOpen} onClose={() => setLegendOpen(false)} />
    </div>
  );
}
